import { useMemo, useState } from "react"

import { useTranslate } from "src/i18n/useTranslate"
import { Combobox, TComboboxBaseOption } from "src/ui/Combobox/Combobox"
import { ErrorService } from "src/utils/ErrorService"

import {
  convertInputValueToOptionLabel,
  findClosestMatch,
  getTimePickerOptions,
  isTMTimePickerInputValue,
  TMTimePickerInputValue,
} from "./timePickerUtils"

export type TMTimePickerInputProps = {
  onChange: (value: TMTimePickerInputValue | null) => void
  label?: string
  initialValue: TMTimePickerInputValue | null
  clockType?: "12" | "24"
  interval?: number
  required?: boolean
  disabled?: boolean
}

export function MTimePickerInput({
  label,
  initialValue,
  clockType,
  interval,
  required,
  onChange,
  disabled,
}: TMTimePickerInputProps) {
  const { t, langKeys } = useTranslate()
  const [inputValue, setInputValue] = useState(
    convertInputValueToOptionLabel(initialValue, clockType ?? "24")
  )

  const options: TComboboxBaseOption[] = useMemo(() => {
    return getTimePickerOptions({
      interval,
      start: 0,
      clockType,
    })
  }, [clockType, interval])

  // The selected option is chosen based on the input value, by finding the
  // closest match using an algorithm that uses levenshtein distance and some
  // normalization of the input value.
  const selectedOption = useMemo(() => {
    if (inputValue.length === 0) {
      return null
    }
    return findClosestMatch(options, inputValue) ?? null
  }, [options, inputValue])

  const selectedValue = selectedOption?.value ?? null

  function handleBlur({
    label,
    value,
  }: {
    label: string | undefined
    value: string | undefined
  }) {
    setInputValue(label ?? "")

    if (!value) {
      onChange(null)
      return
    }

    if (!isTMTimePickerInputValue(value)) {
      ErrorService.captureMessage("Invalid time value", {
        context: {
          data: { label, value, selectedValue, inputValue, options },
        },
      })
      onChange(null)
      return
    }

    onChange(value)
  }

  function handleChange(value: string) {
    if (!isTMTimePickerInputValue(value)) {
      ErrorService.captureMessage("Invalid time value", {
        context: { data: { value, selectedValue, inputValue, options } },
      })
      onChange(null)
      return
    }

    onChange(value)
  }

  function handleInput(value: string) {
    setInputValue(value)
  }

  return (
    <Combobox
      selectedValue={selectedValue}
      label={label ?? t(langKeys.time)}
      options={options}
      input={inputValue}
      required={required}
      onChange={handleChange}
      onInput={handleInput}
      onBlur={handleBlur}
      disabled={disabled}
    />
  )
}
